import React, { useState, useEffect, useRef, useContext } from 'react';
import { Meter, StopRecord } from '../../types';
import { AppContext } from '../../context/AppContext';
import { fetchMeterValue } from '../../services/nodeRedService';
import { ArrowPathIcon, ExclamationTriangleIcon, PhotoIcon, XMarkIcon, CpuChipIcon, PencilSquareIcon } from '@heroicons/react/24/outline';

interface StopModalProps {
  meter: Meter | null;
  isStarting: boolean;
  activeStop?: StopRecord;
  loading?: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
}

type EntryMode = 'auto' | 'manual';

const StopModal: React.FC<StopModalProps> = ({ meter, isStarting, activeStop, loading: externalLoading, onClose, onSubmit }) => {
  const { users } = useContext(AppContext);
  const [reason, setReason] = useState('');
  const [supervisorName, setSupervisorName] = useState('');
  const [value, setValue] = useState('');
  const [photo, setPhoto] = useState<string | null>(null);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoName, setPhotoName] = useState('');
  const [entryMode, setEntryMode] = useState<EntryMode>('auto');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isManual = entryMode === 'manual';

  useEffect(() => {
    if (meter && entryMode === 'auto') {
      setIsLoading(true);
      setError('');
      setValue('');
      fetchMeterValue(meter.id)
        .then(fetchedValue => {
          setValue(fetchedValue.toString());
        })
        .catch(err => {
          setError('Error al obtener valor. Ingrese manualmente.');
          setEntryMode('manual');
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [meter, entryMode]);

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setPhotoFile(file);
      setPhotoName(file.name);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhoto(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isStarting && !reason) {
      setError('El motivo de la parada es obligatorio.');
      return;
    }
    if (isStarting && !supervisorName) {
      setError('El nombre del supervisor es obligatorio.');
      return;
    }
    if (!value) {
      setError('El valor del medidor es obligatorio.');
      return;
    }

    if (!isStarting && activeStop) {
      const numValue = parseFloat(value);
      if (numValue < activeStop.startValue) {
        setError(`El valor final (${numValue}) no puede ser menor al inicial (${activeStop.startValue}).`);
        return;
      }
    }

    onSubmit({
      ...(isStarting && { reason, supervisorName }),
      value: parseFloat(value),
      photo: photo,
      photoFile: photoFile,
      isManual: isManual,
    });
  };

  if (!meter) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-lg relative">
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-500 hover:text-gray-800">
          <XMarkIcon className="h-6 w-6" />
        </button>
        <h2 className="text-2xl font-bold mb-4 text-text-primary">
          {isStarting ? 'Iniciar Parada' : 'Finalizar Parada'} en {meter.name}
        </h2>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4 flex items-center">
            <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">Modo de Entrada</label>
            <div className="grid grid-cols-2 gap-2">
              <button type="button" onClick={() => setEntryMode('auto')} className={`flex items-center justify-center p-3 border rounded-md text-sm font-semibold ${entryMode === 'auto' ? 'bg-brand-primary text-white border-brand-primary' : 'bg-white text-text-primary border-gray-300'}`}> <CpuChipIcon className="h-5 w-5 mr-2" /> Automático </button>
              <button type="button" onClick={() => setEntryMode('manual')} className={`flex items-center justify-center p-3 border rounded-md text-sm font-semibold ${entryMode === 'manual' ? 'bg-brand-primary text-white border-brand-primary' : 'bg-white text-text-primary border-gray-300'}`}> <PencilSquareIcon className="h-5 w-5 mr-2" /> Manual </button>
            </div>
          </div>

          {isStarting && (
            <>
              <div>
                <label htmlFor="supervisorName" className="block text-sm font-medium text-text-secondary">Supervisor Responsable</label>
                <select id="supervisorName" value={supervisorName} onChange={e => setSupervisorName(e.target.value)} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-brand-primary focus:border-brand-primary" required>
                  <option value="" disabled>Seleccione un supervisor</option>
                  {users.map(user => (
                    <option key={user.id} value={user.name}>{user.name} ({user.role})</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="reason" className="block text-sm font-medium text-text-secondary">Motivo de la Parada</label>
                <textarea id="reason" value={reason} onChange={e => setReason(e.target.value)} rows={2} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-brand-primary focus:border-brand-primary" required />
              </div>
            </>
          )}

          <div>
            <label htmlFor="value" className="block text-sm font-medium text-text-secondary">Valor del Totalizador ({meter.unit})</label>
            <div className="mt-1 flex rounded-md shadow-sm">
              <input type="number" step="any" id="value" value={value} onChange={e => setValue(e.target.value)} disabled={!isManual} required className="block w-full border border-gray-300 rounded-l-md p-2 focus:ring-brand-primary focus:border-brand-primary disabled:bg-gray-100" />
              <div className="px-3 py-2 bg-gray-200 text-gray-700 rounded-r-md flex items-center">
                {isLoading ? <ArrowPathIcon className="h-5 w-5 animate-spin" /> : isManual ? <PencilSquareIcon className="h-5 w-5" /> : <CpuChipIcon className="h-5 w-5" />}
              </div>
            </div>
            {!isManual && !error && <p className="text-xs text-green-600 mt-1">Valor obtenido de Node-RED.</p>}
          </div>

          {isManual && (
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">Evidencia Fotográfica (Opcional)</label>
              <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md bg-gray-50 hover:bg-gray-100 transition-colors">
                <div className="space-y-1 text-center">
                  {photo ? (
                    <img src={photo} alt="Evidencia" className="mx-auto h-32 w-auto rounded-lg shadow-sm" />
                  ) : (
                    <PhotoIcon className="mx-auto h-12 w-12 text-gray-400" />
                  )}
                  <div className="flex text-sm text-gray-600 justify-center mt-4">
                    <label htmlFor="file-upload" className="relative cursor-pointer bg-white px-3 py-2 rounded-lg font-medium text-brand-primary border border-gray-200 shadow-sm hover:text-brand-secondary hover:bg-gray-50 transition-all focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-brand-primary">
                      <span>{photo ? 'Cambiar foto / Tomar otra' : 'Subir o Tomar Foto'}</span>
                      <input id="file-upload" name="file-upload" type="file" accept="image/*" capture="environment" className="sr-only" onChange={handlePhotoChange} ref={fileInputRef} />
                    </label>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">{photoName || 'PNG, JPG. Usa la cámara en móviles.'}</p>
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end space-x-4 pt-4 border-t border-gray-100 mt-6">
            <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300">Cancelar</button>
            <button type="submit" className="px-4 py-2 bg-brand-primary text-white rounded-md hover:bg-brand-secondary shadow-md disabled:opacity-50" disabled={isLoading}>{isStarting ? 'Iniciar Parada' : 'Finalizar Parada'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StopModal;